import { Avatar, Card, CardContent, Skeleton, Stack, Typography } from "@mui/material";
import RegionSelector from "../region-selector";
import { useEffect, useMemo, useRef, useState } from "react";
import NonBrandedProvider from "../context/use-non-branded";
import CompetitorsGrid from "../data-grid/competitors-grid";
import { fDate, getLast12WeeksDateRange } from "src/utils/format-time";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/context/store";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { useCompetitorsQuery, useRegionsQuery } from "src/context/api/non-branded/api";
import { useLocales } from "src/locales";
import { setFilterCompare, setFilterDate, setFilterDateCompare, setFilterTimestep } from "src/context/reducers/filter-settings/filterSlice";
import { subDays } from "date-fns";
import { SplashScreen } from "src/components/loading-screen";

type TopCompetitorRow = {
    domain_name: string;
    visibility_rate: number;
};

const TOP_SIZE = 5;
const METRIC_STYLES = {
    visibility: {
        titleColor: "#6259E8",
    },
    paid: {
        titleColor: "#DAA335",
    },
    organic: {
        titleColor: "#18A055",
    },
} as const;

export default function Competitors() {
    const ref = useRef<boolean>(false);
    const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
    const workspace = useCurrentWorkspace();
    const filterStore = useSelector((state: RootState) => state.filters);
    const dispatch = useDispatch();
    const { t } = useLocales();

    const { data: regionsData, isLoading, isFetching, isUninitialized } = useRegionsQuery({
        workspace_id: workspace?.id as number,
        start_date: fDate(filterStore.start, "yyyy-MM-dd"),
        end_date: fDate(filterStore.end, "yyyy-MM-dd"),
        devices: filterStore.devices,
        countries: selectedRegion ? [selectedRegion] : [],
        platforms: filterStore.platforms,
        search_terms: filterStore.keywords,
    }, { skip: !workspace?.id });

    const {
        data: topAllData,
        isLoading: isTopLoading,
        isFetching: isTopFetching,
        isUninitialized: isTopUninitialized,
    } = useCompetitorsQuery({
        query: "top_all",
        workspace_id: workspace?.id as number,
        start_date: fDate(filterStore.start, "yyyy-MM-dd"),
        end_date: fDate(filterStore.end, "yyyy-MM-dd"),
        devices: filterStore.devices,
        countries: selectedRegion ? [selectedRegion] : [],
        platforms: filterStore.platforms,
        search_terms: filterStore.keywords,
        page_size: 375,
        page: 0,
    }, { skip: !workspace?.id });

    useEffect(() => {
        const { start, end } = getLast12WeeksDateRange();
        dispatch(setFilterDate({ start, end }));
        dispatch(setFilterCompare(false))
        dispatch(setFilterTimestep("year_week"))


        dispatch(setFilterDateCompare({
            compareStart: subDays(new Date(), 60).toISOString(),
            compareEnd: subDays(new Date(), 31).toISOString(),
        }))
        ref.current = true;
    }, []);

    const topRows = (topAllData?.data || []) as TopCompetitorRow[];
    const topVisibility = useMemo(() => topRows.filter((row: any) => row.type === 'visibility'), [topRows]);
    const topPaid = useMemo(() => topRows.filter((row: any) => row.type === 'paid'), [topRows]);
    const topOrganic = useMemo(() => topRows.filter((row: any) => row.type === 'organic'), [topRows]);


    useEffect(() => {
        if (filterStore?.countries?.length > 0) {
            setSelectedRegion(filterStore?.countries[0]);
        }
    }, [filterStore?.countries]);

    const renderTopCard = (
        title: string,
        rows: TopCompetitorRow[],
        metric: keyof typeof METRIC_STYLES
    ) => (
        <Stack direction="column" width="33%" spacing={1}>
            <Stack px={0}>
                <Typography
                    variant="body1"
                    sx={{
                        color: METRIC_STYLES[metric].titleColor,
                        fontSize: "14px",
                        fontWeight: 600,
                        lineHeight: "120%",
                        textTransform: "uppercase",
                    }}
                >
                    {title}
                </Typography>
            </Stack>
            <Stack px={0} py={1.5} spacing={1}>
                {(isTopLoading || isTopFetching || isTopUninitialized) ? (
                    <Skeleton variant="rectangular" height={150} />
                ) : rows.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        {t("dashboard.nonBrand.noCompetitorsDataAvailable")}
                    </Typography>
                ) : (
                    <>
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{
                                py: 0.75,
                                px: 1,
                                pr: 1.5,
                                borderRadius: 1,
                                bgcolor: "#FFFF",
                            }}
                            border={"1px solid rgba(145, 158, 171, 0.10)"}
                        >
                            <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: "flex",
                                        padding: "2px 6px",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        gap: "4px",
                                        color: "rgba(0, 108, 156, 0.85)",
                                        fontVariantNumeric: "lining-nums tabular-nums",
                                        fontFamily: "Public Sans",
                                        fontSize: 13,
                                        fontWeight: 400,
                                        lineHeight: "120%",
                                        borderRadius: "4px",
                                        background: "rgba(177, 212, 247, 0.20)",
                                    }}
                                >
                                    #{rows.findIndex(row => row.domain_name === workspace?.url || workspace?.friendlyDomains?.includes(row.domain_name)) + 1}
                                </Typography>
                                <Avatar
                                    src={`https://www.google.com/s2/favicons?domain=${rows.find(row => row.domain_name === workspace?.url || workspace?.friendlyDomains?.includes(row.domain_name))?.domain_name}&sz=32`}
                                    sx={{ width: 18, height: 18 }}
                                />
                                <Typography variant="body2" noWrap sx={{
                                    color: "#333C47",
                                    fontFamily: "Public Sans",
                                    fontSize: 13,
                                    fontWeight: 500,
                                    lineHeight: "100%",
                                }}>
                                    {rows.find(row => row.domain_name === workspace?.url || workspace?.friendlyDomains?.includes(row.domain_name))?.domain_name}
                                </Typography>
                            </Stack>
                            <Typography variant="body2" sx={{
                                color: "#333C47",
                                fontFamily: "Public Sans",
                                fontSize: 13,
                                fontWeight: 400,
                                lineHeight: "100%",
                            }}>
                                {rows.find(row => row.domain_name === workspace?.url || workspace?.friendlyDomains?.includes(row.domain_name))?.visibility_rate}%
                            </Typography>
                        </Stack>

                        <Typography
                            variant="caption"
                            sx={{
                                color: "#637381",
                                fontWeight: 700,
                                letterSpacing: "0.08em",
                                textTransform: "uppercase",
                                pt: 0.5,
                            }}
                        >
                            Top 5 Players
                        </Typography>

                        <Stack sx={{
                            borderRadius: "8px",
                            border: "1px solid rgba(145, 158, 171, 0.13)",
                            background: "#FFFF",
                        }}>
                            {rows.slice(0, TOP_SIZE).map((row, index) => (
                                <Stack
                                    key={`${title}-${row.domain_name}-${index}`}
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    spacing={1}
                                    sx={{
                                        display: "flex",
                                        padding: "12px",
                                        alignItems: "center",
                                        gap: "8px",
                                        alignSelf: "stretch",
                                        borderRadius: index === 0 ? "8px 8px 0 0" : index === rows.length - 1 ? "0 0 8px 8px" : "unset",
                                        background: row.domain_name === workspace?.url || workspace?.friendlyDomains?.includes(row.domain_name) ? "rgba(226, 229, 233, 0.25)" : "unset",
                                        borderBottom: index !== rows.length - 1 ? "1px dashed rgba(145, 158, 171, 0.07)" : "none",
                                    }}
                                >
                                    <Stack direction="row" sx={{

                                    }} spacing={1} alignItems="center" minWidth={0}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                display: "flex",
                                                padding: "2px 6px",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                gap: "4px",
                                                color: "rgba(0, 108, 156, 0.85)",
                                                fontVariantNumeric: "lining-nums tabular-nums",
                                                fontFamily: "Public Sans",
                                                fontSize: 13,
                                                fontWeight: 400,
                                                lineHeight: "120%",
                                                borderRadius: "4px",
                                                background: "rgba(177, 212, 247, 0.20)",
                                            }}
                                        >
                                            #{index + 1}
                                        </Typography>
                                        <Avatar
                                            src={`https://www.google.com/s2/favicons?domain=${row.domain_name}&sz=32`}
                                            sx={{ width: 18, height: 18 }}
                                        />
                                        <Typography variant="body2" noWrap sx={{ color: "#333C47" }}>
                                            {row.domain_name}
                                        </Typography>
                                    </Stack>
                                    <Typography variant="body2" sx={{

                                        color: "#333C47",
                                        fontFamily: "Public Sans",
                                        fontSize: 13,
                                        fontWeight: 400,
                                        lineHeight: "100%",
                                    }}>
                                        {Math.round(row.visibility_rate)}%
                                    </Typography>
                                </Stack>
                            ))}
                        </Stack>
                    </>
                )
                }
            </Stack >
        </Stack >
    );
    if (!ref.current) return <SplashScreen />;

    return (
        <NonBrandedProvider values={{ selectedRegion, setSelectedRegion, selectedOpportunity: null, setSelectedOpportunity: () => { } }}>
            <RegionSelector data={regionsData?.data || []} loading={isLoading || isFetching || isUninitialized} />
            <Stack direction="column" spacing={"28px"} px={"24px"} mt={"24px"}>
                <Stack px={"24px"} direction="row" justifyContent="space-between" spacing={2}>
                    {renderTopCard(t("dashboard.nb_brand_grid.columns.title.visibility_rate"), topVisibility, "visibility")}
                    {renderTopCard(t("dashboard.nb_brand_grid.columns.title.paid_visibility_rate"), topPaid, "paid")}
                    {renderTopCard(t("dashboard.nb_brand_grid.columns.title.organic_visibility_rate"), topOrganic, "organic")}
                </Stack>
                <Stack px={"24px"}>
                    <CompetitorsGrid />
                </Stack>
            </Stack>
        </NonBrandedProvider>
    );
}